/**
 * drawerClientSync — keep the open launch form's client in step with the
 * client the pane SHOWS (story 28633).
 *
 * Two pieces of state hold "the client": the client hook (`useClientContext`,
 * what the client box renders) and the drawer's own `clientSk` (what the
 * "Select a client to continue" gate checks and the launch sends). The drawer's
 * copy used to be set only when it opened and on a manual pick, so a client
 * resolved AFTER the drawer opened — the per-email identify run on a reopened
 * email, or a lookup by name — appeared in the box while the form still had
 * none: a client on screen with "Select a client to continue." under it.
 *
 * Pure, so the rule is testable without mounting App.
 */

export interface DrawerClientFields {
  open: boolean;
  mode: string;
  clientSk?: number | null;
  clientName?: string | null;
  /** Set when the brief belongs to ANOTHER email than the one selected. */
  briefEmail?: unknown | null;
}

/**
 * The drawer state after the client hook resolved `client`. Returns `prev`
 * itself when nothing changes, so a React state setter can bail out.
 *
 * - An open launch form with NO client adopts the resolved one — unless the
 *   brief is another email's: then the hook's client is the SELECTED email's,
 *   which must never become this brief's client.
 * - A form already on this client back-fills its name.
 * - A form on a different client is never touched (the AM's pick wins).
 */
export function syncDrawerClient<T extends DrawerClientFields>(
  prev: T,
  client: { client_sk: number; client_name: string } | null | undefined,
): T {
  if (!client || !prev.open || prev.mode !== "pre_launch") return prev;
  if (prev.clientSk == null) {
    if (prev.briefEmail || !client.client_sk) return prev;
    return { ...prev, clientSk: client.client_sk, clientName: client.client_name };
  }
  if (prev.clientSk === client.client_sk && prev.clientName !== client.client_name) {
    return { ...prev, clientName: client.client_name };
  }
  return prev;
}
